import { Field, FieldProps, Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { FormattedMessage } from 'react-intl';
import { Grid } from '@mui/material';
import { ObjectWithId } from '@synergy/models';
import { PoolButton, PoolModal, PoolTextField, PoolTypography } from '@synergy/components';
import { PoolNumberTextfield } from '@synergy/components-next';
import { fields } from '@synergy/utils';

import * as PaymentTermActions from './store/payment-terms-actions';
import { ConfigurationTranslationsField } from '../../components/configuration-translations-field';
import { PaymentTerm } from '../../../../common/models/payment-term';
import { PaymentTermFormikValues } from './models/payment-term-formik-values';
import { paymentTermsModalValidationSchema } from './payment-terms-detail-modal-validation-schema';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

interface PaymentTermDetailModalProps {
    visible: boolean;
    paymentTerm: PaymentTerm | undefined;
    onClose: () => void;
}

export const PaymentTermDetailModal = (props: PaymentTermDetailModalProps) => {
    const dispatch = useThunkDispatch();
    const availableLocales = useAppSelector(state => state.supportedLocales);
    const currentLocale = useAppSelector(state => state.currentLocale);
    const fieldNames = fields<PaymentTermFormikValues>();

    const gridItemSize = 4;

    const handleSubmit = (values: PaymentTermFormikValues, formikProps: FormikHelpers<PaymentTermFormikValues>) => {
        props.onClose();

        const paymentTerms: Partial<PaymentTerm> & ObjectWithId = {
            id: values.id,
            days: values.days ?? 0,
            discount: values.discount ?? 0,
            netDays: values.netDays ?? 0,
            text: values.text,
        };

        if (values.id > 0) {
            dispatch(PaymentTermActions.updatePaymentTerm(paymentTerms));
        } else {
            dispatch(PaymentTermActions.createPaymentTerm(paymentTerms));
        }

        formikProps.resetForm();
    };

    const handleClose = (formikProps: FormikProps<PaymentTermFormikValues>) => {
        formikProps.resetForm();
        props.onClose();
    };

    const initialValues: PaymentTermFormikValues = {
        id: -1,
        text: { de: '' },
        days: null,
        discount: null,
        netDays: null,
        ...props.paymentTerm,
    };

    const actionChildren = (formikProps: FormikProps<PaymentTermFormikValues>) => (
        <>
            <PoolButton key="1" styleType="firstLevel" variant="text" onClick={() => handleClose(formikProps)}>
                <FormattedMessage id="app.common.cancel" />
            </PoolButton>
            <PoolButton key="2" styleType="firstLevel" onClick={() => formikProps.submitForm()}>
                <FormattedMessage id={formikProps.values.id > 0 ? 'app.common.save' : 'app.common.add'} />
            </PoolButton>
        </>
    );

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            enableReinitialize={true}
            validationSchema={paymentTermsModalValidationSchema(currentLocale)}
        >
            {(formikProps: FormikProps<PaymentTermFormikValues>) => (
                <Form>
                    <PoolModal
                        title={
                            <FormattedMessage
                                id={
                                    formikProps.values.id > 0
                                        ? 'app.configuration.paymentTerms.edit'
                                        : 'app.configuration.paymentTerms.create'
                                }
                            />
                        }
                        isVisible={props.visible}
                        handleClose={() => handleClose(formikProps)}
                        actionChildren={actionChildren(formikProps)}
                        width="lg"
                    >
                        <Grid container={true} spacing={2} sx={{ mt: 1 }}>
                            <Grid item={true} xs={12}>
                                <PoolTypography styleType="sectionTitleNew">
                                    <FormattedMessage id={`app.configuration.paymentTerms.conditions`} />
                                </PoolTypography>
                            </Grid>
                            <Grid item={true} xs={gridItemSize}>
                                <Field name={fieldNames.days}>
                                    {({ field, meta }: FieldProps<string>) => (
                                        <PoolTextField
                                            {...field}
                                            isNumeric={true}
                                            value={field.value ?? ''}
                                            fullWidth={true}
                                            required={true}
                                            label={<FormattedMessage id={`app.configuration.paymentTerms.days`} />}
                                            error={meta.touched && !!meta.error}
                                            helperText={
                                                meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                            }
                                            autoFocus={true}
                                        />
                                    )}
                                </Field>
                            </Grid>
                            <Grid item={true} xs={gridItemSize}>
                                <Field name={fieldNames.discount}>
                                    {({ field, meta }: FieldProps<string>) => (
                                        <PoolTextField
                                            {...field}
                                            value={field.value ?? ''}
                                            fullWidth={true}
                                            required={true}
                                            isNumeric={true}
                                            suffix=" %"
                                            label={<FormattedMessage id={`app.configuration.paymentTerms.discount`} />}
                                            error={meta.touched && !!meta.error}
                                            helperText={
                                                meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                            }
                                        />
                                    )}
                                </Field>
                            </Grid>
                            <Grid item={true} xs={gridItemSize}>
                                <Field name={fieldNames.netDays}>
                                    {({ field, meta }: FieldProps<string>) => (
                                        <PoolTextField
                                            {...field}
                                            isNumeric={true}
                                            value={field.value ?? ''}
                                            fullWidth={true}
                                            required={true}
                                            label={<FormattedMessage id={`app.configuration.paymentTerms.netDays`} />}
                                            error={meta.touched && !!meta.error}
                                            helperText={
                                                meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                            }
                                        />
                                    )}
                                </Field>
                            </Grid>
                            <Grid item={true} xs={12}>
                                <PoolTypography styleType="sectionTitleNew">
                                    <FormattedMessage id={`app.configuration.paymentTerms.text`} />
                                </PoolTypography>
                            </Grid>
                            {availableLocales.map(locale => {
                                return (
                                    <Grid key={locale} item={true} xs={12}>
                                        <ConfigurationTranslationsField locale={locale} fieldName={fieldNames.text} />
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </PoolModal>
                </Form>
            )}
        </Formik>
    );
};
